import { mdiAccount } from '@mdi/js';
import { Currency } from 'common/classes/currency';
import { StyledButton } from 'common/components/buttons';
import { BaseTable, ColumnProp } from 'common/components/table/base';
import { TableTitle } from 'common/components/table/title';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import { accountCustomersRecipe } from 'common/state/accounts/recipes';
import { defaultAccountCustomersState } from 'common/state/accounts/states';
import { doGetAccountCustomers } from 'common/state/accounts/thunkActions';
import { CustomerSummary } from 'common/state/customers/actions';
import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { ModalMode } from '../rencocile';
import { SettleRow } from './modalCards';

export interface CustomersTableProps {
  setSelectedRow: (customer: SettleRow) => void;
  setModalMode: (mode: ModalMode) => void;
  shouldRefresh: boolean;
  customerSummaries: CustomerSummary[];
  isLoading?: boolean;
  error?: React.ReactNode;
}

export const MovementsLink = styled(Link)`
  cursor: pointer;
  color: inherit;
  text-decoration: none;

  .value {
    text-decoration: underline;
  }
`;

const AlertKeyframes = ({ theme }) => keyframes`
  0% { color: ${theme.foreground} }
  50% { color: ${theme.warning} }
  100% { color: ${theme.foreground} }
`;

export const ThresholdContainer = styled.div<{ alert?: boolean }>`
  .currency .value {
    ${(props) =>
      props.alert
        ? css`
            animation: ${AlertKeyframes(props)} 2s infinite;
          `
        : undefined}
  }
`;

export const CustomersTable = ({
  setSelectedRow,
  setModalMode,
  shouldRefresh,
  customerSummaries,
  isLoading,
  error,
}: CustomersTableProps) => {
  const [customersState, customersDispatch] = useProducerWithThunks(
    accountCustomersRecipe,
    defaultAccountCustomersState
  );

  useEffect(() => {
    customersDispatch(doGetAccountCustomers());
  }, [customersDispatch]);

  useEffect(() => {
    if (shouldRefresh) {
      customersDispatch(doGetAccountCustomers());
    }
  }, [customersDispatch, shouldRefresh]);

  const columns = useMemo((): ColumnProp<CustomerSummary>[] => {
    const mappedCustomers =
      customersState.customers?.reduce(
        (acc, customer) => ({
          ...acc,
          [customer.account_code.split('.').slice(-4).join('.')]: customer,
        }),
        {}
      ) || {};
    const getCustomer = (summary: CustomerSummary) => {
      const accountKey = `customer.${summary.customer_id}.client.${summary.wallet_currency}`;
      return mappedCustomers[accountKey];
    };

    return [
      { key: 'customer_name', title: 'Name' },
      { key: 'wallet_currency', title: 'Currency' },
      {
        key: 'balance',
        title: 'Balance',
        valueFormatter: (row) => {
          const value = new Currency(
            row.balances.final_balance,
            row.wallet_currency
          ).toElement();
          const customer = getCustomer(row);
          return customer ? (
            <MovementsLink to={`/movements/${customer.account_code}`}>
              {value}
            </MovementsLink>
          ) : (
            value
          );
        },
        csvFormatter: (row) =>
          new Currency(
            row.balances.final_balance,
            row.wallet_currency
          ).toString({ symbol: false }),
        sortValueGenerator: (row) => parseFloat(row.balances.final_balance),
      },
      {
        key: 'reconciled',
        title: 'Reconciled',
        valueFormatter: (row) => {
          const value = new Currency(
            row.balances.reconciled,
            row.wallet_currency
          ).toElement();
          const customer = getCustomer(row);
          return customer ? (
            <MovementsLink
              to={`/movements/${customer.account_code.replace(
                'client',
                'reconciled'
              )}`}
            >
              {value}
            </MovementsLink>
          ) : (
            value
          );
        },
        csvFormatter: (row) =>
          new Currency(row.balances.reconciled, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row) => parseFloat(row.balances.reconciled),
      },
      {
        key: 'threshold',
        title: 'Threshold',
        valueFormatter: (row) => (
          <ThresholdContainer
            alert={
              Math.abs(parseFloat(row.balances.reconciled)) >
              parseFloat(row.wallet_threshold)
            }
          >
            {new Currency(row.wallet_threshold, row.wallet_currency).toElement({
              colour: false,
            })}
          </ThresholdContainer>
        ),
        csvFormatter: (row) =>
          new Currency(row.wallet_threshold, row.wallet_currency).toString({
            symbol: false,
          }),
        sortValueGenerator: (row) => parseFloat(row.wallet_threshold),
      },
      {
        key: 'actions',
        title: 'Actions',
        valueFormatter: (row) => (
          <StyledButton
            onClick={() => {
              setSelectedRow({
                username: row.customer_username,
                currency: row.wallet_currency,
                amount: row.balances.reconciled,
              });
              setModalMode('customerSettle');
            }}
          >
            Settle
          </StyledButton>
        ),
        isSortable: false,
      },
    ];
  }, [setSelectedRow, setModalMode, customersState.customers]);

  return (
    <>
      <TableTitle icon={mdiAccount} title="Customers" />
      <br />
      <BaseTable
        columns={columns}
        rows={customerSummaries || []}
        csvName="Customer"
        isLoading={isLoading}
        error={error}
      />
    </>
  );
};
